import React from 'react';
import type { TextChunk } from './groq';

/**
 * Extract all citation numbers from a citation like [4, 5, 88-94]
 */
export function extractCitationNumbers(citation: string): number[] {
  const numbers: number[] = [];
  // Match individual numbers and ranges like "88-94" or "88–94"
  const parts = citation.match(/\d+(?:\s*[-–]\s*\d+)?/g) || [];

  for (const part of parts) {
    if (part.includes('-') || part.includes('\u2013')) {
      // It's a range
      const [start, end] = part.split(/[-\u2013]/).map(n => parseInt(n.trim(), 10));
      for (let i = start; i <= end; i++) {
        numbers.push(i);
      }
    } else {
      numbers.push(parseInt(part, 10));
    }
  }
  return numbers;
}

/**
 * Parse citations and make them clickable
 */
export function renderTextWithCitations(
  text: string,
  onCitationClick: (index: number, allIndices: number[], event: React.MouseEvent) => void
): React.ReactNode {
  const citationRegex = /(\[\d+(?:\s*[,\-\u2013]\s*\d+)*\])/gi;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = citationRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const indices = extractCitationNumbers(match[1]);

    if (indices.length > 0) {
      const tooltip = indices.length > 1
        ? 'Click to choose source'
        : 'Jump to source';

      parts.push(
        React.createElement('span', {
          key: match.index,
          className: 'citation-badge',
          onClick: (e: React.MouseEvent) => onCitationClick(indices[0], indices, e),
          'data-tooltip': tooltip,
        }, '\u2197')
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

/**
 * Get text preview for a chunk index
 */
export function getChunkPreview(chunks: TextChunk[], index: number, maxLength: number = 50): string {
  const chunk = chunks.find(c => c.index === index);
  if (!chunk) return `Source ${index}`;
  const text = chunk.textStart || chunk.text.slice(0, maxLength);
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

/**
 * Storage key for chat history
 */
export function getChatStorageKey(url: string): string {
  return `chat_${url}`;
}
